import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { MetricCard } from '@/components/ui/metric-card';
import { DIDList, DIDDetailDrawer, E911ComplianceDrawer, QuickReportModal, useDIDs, useStaleDIDsCount, useStaleDIDsList, useTelviaIQOrders, useDIDStore } from '@/features/dids';
import type { DID, TelviaIQOrder } from '@/features/dids';
import { isEmbedMode, formatPhoneNumber } from '@/lib/utils';
import { Download, Plus, Phone, TrendingUp, BellRing, ZapOff, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';

export function DIDsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEmbed = isEmbedMode();
  const { data: dids } = useDIDs();
  const { data: staleData } = useStaleDIDsCount();
  const { data: recentOrders = [] } = useTelviaIQOrders({ limit: 500 });
  const { openDetailModal, staleFilterActive, setStaleFilter, clearStaleFilter } = useDIDStore();

  // State for triggering stale list fetch
  const [shouldFetchStaleList, setShouldFetchStaleList] = useState(false);
  const { data: staleListData } = useStaleDIDsList(30, shouldFetchStaleList);

  // E911 Compliance Drawer state
  const [isE911DrawerOpen, setIsE911DrawerOpen] = useState(false);

  // Quick Report Modal state
  const [isReportOpen, setIsReportOpen] = useState(false);

  // Handle deep-linking: ?did=XXX&drawer=config opens DID detail drawer
  useEffect(() => {
    const didParam = searchParams.get('did');
    const drawerParam = searchParams.get('drawer');

    if (didParam && drawerParam === 'config') {
      openDetailModal(didParam, 'failover');
      // Clear params so drawer can be closed
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, openDetailModal]);

  // Handle deep-linking: ?view=911-compliance opens drawer on mount
  useEffect(() => {
    if (searchParams.get('view') === '911-compliance') {
      setIsE911DrawerOpen(true);
      // Clear the param so drawer can be closed
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // When stale list data is fetched, apply the filter
  useEffect(() => {
    if (staleListData?.stale_dids && shouldFetchStaleList) {
      setStaleFilter(true, staleListData.stale_dids);
      setShouldFetchStaleList(false);
    }
  }, [staleListData, shouldFetchStaleList, setStaleFilter]);

  // Handler for clicking on Tile 4 (Inventory Utilization)
  const handleStaleFilterClick = () => {
    if (staleFilterActive) {
      // Already filtered - clear it
      clearStaleFilter();
    } else {
      // Trigger fetch of stale list, then apply filter
      setShouldFetchStaleList(true);
    }
  };

  // Calculate 30-day changes - count ALL successful orders
  const recentChanges = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    let added = 0;
    let removed = 0;
    let total = 0;

    recentOrders.forEach((order: TelviaIQOrder) => {
      const orderDate = new Date(order.created_at);
      if (orderDate >= thirtyDaysAgo && order.status === 'SUCCESS') {
        total += 1;
        if (order.order_type === 'DID_ORDER') {
          added += order.port_dids?.length || 1;
        } else if (order.order_type === 'DID_RELEASE') {
          removed += order.port_dids?.length || 1;
        }
      }
    });

    return { added, removed, total };
  }, [recentOrders]);

  // Compute inventory metrics from DID data
  const metrics = useMemo(() => {
    if (!dids || dids.length === 0) {
      return {
        total: 0,
        local: 0,
        tollFree: 0,
        withServices: 0,
        withForward: 0,
        withCallerId: 0,
        withSms: 0,
        withFax: 0,
        withE911: 0,
        withAlerts: 0,
        silentE911: 0,
      };
    }

    const local = dids.filter((d: DID) => d.type === 'Local' || !d.tn?.startsWith('8')).length;
    const tollFree = dids.length - local;

    // Count DIDs with at least one service
    const withServices = dids.filter(
      (d: DID) =>
        d.has_forward ||
        d.has_caller_id ||
        d.sms ||
        d.has_fax_to_email ||
        d.has_conferencing
    ).length;

    const withForward = dids.filter((d: DID) => d.has_forward).length;
    const withCallerId = dids.filter((d: DID) => d.has_caller_id).length;
    const withSms = dids.filter((d: DID) => d.sms).length;
    const withFax = dids.filter((d: DID) => d.has_fax_to_email).length;

    // E911 metrics
    const withE911 = dids.filter((d: DID) => d.has_911).length;
    const withAlerts = dids.filter((d: DID) => d.has_911 && d.e911_has_alerts).length;
    const silentE911 = dids.filter((d: DID) => d.has_911 && !d.e911_has_alerts).length;

    return {
      total: dids.length,
      local,
      tollFree,
      withServices,
      withForward,
      withCallerId,
      withSms,
      withFax,
      withE911,
      withAlerts,
      silentE911,
    };
  }, [dids]);

  const handleExportCSV = () => {
    if (!dids || dids.length === 0) {
      toast.error('No DIDs to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Phone Number',
      'Status',
      'Rate Center',
      'State',
      'CNAM',
      'SMS',
      'E911',
      'Caller ID',
      'Forwarding',
      'Failover',
      'eFax',
      'Conference',
    ];

    // Map DIDs to CSV rows
    const rows = dids.map((did: DID) => [
      formatPhoneNumber(did.tn),
      did.status || 'Active',
      did.rate_center || '',
      did.state || '',
      did.cnam ? 'Yes' : 'No',
      did.sms ? 'Yes' : 'No',
      did.has_911 ? 'Yes' : 'No',
      did.has_caller_id ? 'Yes' : 'No',
      did.has_forward ? 'Yes' : 'No',
      did.has_failover ? 'Yes' : 'No',
      did.has_fax_to_email ? 'Yes' : 'No',
      did.has_conferencing ? 'Yes' : 'No',
    ]);

    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => {
          // Escape cells containing commas or quotes
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      ),
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `telviaiq-dids-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${dids.length} DIDs to CSV`);
  };

  // Build recent changes display - total orders this month
  const recentChangesValue = String(recentChanges.total);
  const recentChangesSubtitle = recentChanges.total === 0
    ? 'No changes this month'
    : recentChanges.total === 1
      ? '1 change this month'
      : `${recentChanges.total} changes this month`;

  // Build E911 subtitle
  const e911Subtitle = [
    metrics.withAlerts > 0 && `${metrics.withAlerts} with Alerts`,
    metrics.silentE911 > 0 && `${metrics.silentE911} Silent`,
  ]
    .filter(Boolean)
    .join(' \u2022 ');

  return (
    <div>
      <PageHeader title="Phone Numbers" />

      <div className="p-6">
        {/* Inventory Snapshot Bar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MetricCard
            icon={Phone}
            label="DID Composition"
            value={String(metrics.total)}
            subtitle={`${metrics.local} Local \u2022 ${metrics.tollFree} Toll-Free`}
            variant="compact"
            iconColor="indigo"
          />
          <MetricCard
            icon={TrendingUp}
            label="Recent Changes"
            value={recentChangesValue}
            subtitle={recentChangesSubtitle}
            variant="compact"
            iconColor="indigo"
            onClick={() => navigate('/order-history')}
          />
          <MetricCard
            icon={BellRing}
            label="E911 Visibility"
            value={String(metrics.withE911)}
            subtitle={e911Subtitle || 'No E911 configured'}
            variant="compact"
            iconColor="indigo"
            alert={
              metrics.silentE911 > 0
                ? { count: metrics.silentE911, color: 'rose' }
                : undefined
            }
            onClick={() => setIsE911DrawerOpen(true)}
          />
          <MetricCard
            icon={ZapOff}
            label="Inventory Utilization"
            value={String(staleData?.stale_count ?? 0)}
            subtitle={
              staleData?.stale_count
                ? `Numbers with 0 traffic (30d)`
                : 'All DIDs active'
            }
            variant="compact"
            iconColor="purple"
            tooltip="Click to filter table to stale DIDs"
            onClick={handleStaleFilterClick}
            alert={
              staleFilterActive
                ? { count: staleData?.stale_count ?? 0, color: 'indigo' }
                : undefined
            }
          />
        </div>

        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Active Inventory</h2>
            <p className="text-sm text-slate-500 mt-1">
              Manage your entire DID fleet from a single pro-data table.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => setIsReportOpen(true)}
              className="border-slate-200"
            >
              <FileSpreadsheet size={16} className="mr-2" />
              Quick Report
            </Button>
            <Button
              variant="secondary"
              onClick={handleExportCSV}
              className="border-slate-200"
            >
              <Download size={16} className="mr-2" />
              Export CSV
            </Button>
            {!isEmbed && (
              <Button
                onClick={() => navigate('/dids/search')}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 text-white shadow-indigo"
              >
                <Plus size={16} className="mr-2" />
                Purchase DIDs
              </Button>
            )}
          </div>
        </div>

        {/* DID Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <DIDList />
        </div>
      </div>

      {/* DID Detail Drawer */}
      <DIDDetailDrawer />

      {/* E911 Compliance Drawer */}
      <E911ComplianceDrawer
        isOpen={isE911DrawerOpen}
        onClose={() => setIsE911DrawerOpen(false)}
      />

      {/* Quick Report Modal */}
      <QuickReportModal
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
      />
    </div>
  );
}
